import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { DecorativeDivider, OmMark, Mandala } from "@/components/Ornaments";

export default function Contact() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ name: "", mobile: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/settings/public").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 600));
    toast.success(t("contact.thanks"));
    setForm({ name: "", mobile: "", email: "", message: "" });
    setSubmitting(false);
  };

  return (
    <div data-testid="contact-page">
      <section className="section bg-paper relative overflow-hidden">
        <div className="absolute -right-32 top-12 text-[color:var(--brand)] pointer-events-none hidden md:block">
          <Mandala size={520} opacity={0.05} />
        </div>
        <div className="container-x grid md:grid-cols-2 gap-12 relative">
          <div>
            <OmMark size={36} className="text-[color:var(--brand)] mb-3" />
            <DecorativeDivider label={t("nav.contact")} className="mb-5 justify-start" />
            <h1 className="font-heading text-5xl md:text-7xl tracking-display leading-[0.98]">
              Reach <em className="italic-serif text-[color:var(--brand)]">Acharya Guruji</em>
            </h1>
            <p className="mt-5 opacity-80 max-w-md">{t("contact.subtitle")}</p>

            <div className="mt-10 space-y-5">
              {settings?.contact_phone && (
                <a href={`tel:${settings.contact_phone.replace(/\s/g, "")}`} className="flex items-center gap-4 group" data-testid="contact-phone">
                  <span className="w-11 h-11 rounded-full flex items-center justify-center border border-[color:var(--brand)]/30" style={{ background: "rgba(142,43,37,0.06)", color: "var(--brand)" }}><Phone size={16} /></span>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] opacity-60">{t("common.phone")}</div>
                    <div className="font-heading text-lg group-hover:text-[color:var(--brand)]">{settings.contact_phone}</div>
                  </div>
                </a>
              )}
              {settings?.contact_whatsapp && (
                <a href={`https://wa.me/${settings.contact_whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-4 group" data-testid="contact-whatsapp">
                  <span className="w-11 h-11 rounded-full flex items-center justify-center border border-[color:var(--brand)]/30" style={{ background: "rgba(142,43,37,0.06)", color: "var(--brand)" }}><MessageCircle size={16} /></span>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] opacity-60">{t("common.whatsapp")}</div>
                    <div className="font-heading text-lg group-hover:text-[color:var(--brand)]">{settings.contact_whatsapp}</div>
                  </div>
                </a>
              )}
              {settings?.contact_email && (
                <a href={`mailto:${settings.contact_email}`} className="flex items-center gap-4 group" data-testid="contact-email">
                  <span className="w-11 h-11 rounded-full flex items-center justify-center border border-[color:var(--brand)]/30" style={{ background: "rgba(142,43,37,0.06)", color: "var(--brand)" }}><Mail size={16} /></span>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] opacity-60">{t("common.email")}</div>
                    <div className="font-heading text-lg group-hover:text-[color:var(--brand)]">{settings.contact_email}</div>
                  </div>
                </a>
              )}
              {settings?.contact_address && (
                <div className="flex items-start gap-4">
                  <span className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 border border-[color:var(--brand)]/30" style={{ background: "rgba(142,43,37,0.06)", color: "var(--brand)" }}><MapPin size={16} /></span>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] opacity-60">{t("common.address")}</div>
                    <div className="opacity-90 leading-relaxed italic-serif">{settings.contact_address}</div>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-10 frame-gilt">
              <div className="aspect-video overflow-hidden">
                <iframe title="Map" src="https://maps.google.com/maps?q=Trimbakeshwar%20Temple&t=&z=14&ie=UTF8&iwloc=&output=embed" width="100%" height="100%" loading="lazy"></iframe>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="card-manuscript rounded-md p-8 h-fit" data-testid="contact-form">
            <DecorativeDivider label="Send a Message" className="mb-5 justify-start" />
            <h2 className="font-heading text-3xl">{t("contact.formTitle")}</h2>
            <div className="gilt-rule my-5 w-16" />
            <div className="space-y-4">
              <Input placeholder={t("contact.name")} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" />
              <Input placeholder={t("contact.mobile")} required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="contact-mobile-input" />
              <Input placeholder={t("contact.emailField")} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
              <Textarea placeholder={t("contact.message")} rows={5} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" />
              <button type="submit" disabled={submitting} className="w-full px-5 py-3 rounded-full btn-brand text-sm disabled:opacity-60 font-medium" data-testid="contact-submit-btn">
                {submitting ? t("common.loading") : t("contact.send")}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
